import { isoDay } from 'helper/timeLabels';
import { read, write } from './crmStore';
import { companyOf, personOf, withCompany } from './crmViews';

/**
 * The demo of the planning: the appointments, tasks and visits to place of a
 * week, and what places, moves, creates or closes an appointment, answered
 * from the demo database (crmMockData.js). crmApi.js sends these calls to the
 * api, except placeVisit (the visits to place come with their own back):
 *
 *   getPlanning         POST admin/crm/planning              { from, to }                               api
 *   placeVisit          POST admin/crm/planning/place        { visitId, startsAt, travelMinutes }       demo
 *   removeAppointment   POST admin/crm/appointment/delete    { id, scope }  (a placed visit goes back to place)  api or demo, by id
 *   createAppointment   POST admin/crm/appointment/create    { ownerId, category, title, description,
 *                                                              allDay, startDay, endDay, startsAt, endsAt,
 *                                                              companyId, location, mode, contactName,
 *                                                              attendees, meet, reminders, colorId,
 *                                                              transparency, visibility, recurrence }   api
 *   getAppointment      POST admin/crm/appointment           { id }                                     api or demo, by id
 *   updateAppointment   POST admin/crm/appointment/update    { id, scope, fields: { status, outcome,
 *                                                              title, location, allDay, startDay, endDay,
 *                                                              startsAt, endsAt, description, mode,
 *                                                              attendees, meet, reminders, colorId,
 *                                                              transparency, visibility, recurrence } } api or demo, by id
 *   respondAppointment  POST admin/crm/appointment/respond   { id, response }                           api or demo, by id
 *
 * attendees is the whole list: [{ userId }] for a colleague, [{ email,
 * displayName?, contactId? }] for anyone else. reminders is { useDefault,
 * overrides: [{ method: 'popup' | 'email', minutes }] } (5 at most), colorId
 * '' or '1' to '11' (constants/appointmentColors.js), transparency 'opaque' |
 * 'transparent', visibility 'default' | 'public' | 'private'. response is
 * 'accepted' | 'declined' | 'tentative'; the api answers { id, response }.
 *
 * A series (helper/recurrence.js): recurrence is the rule of the series,
 * absent or null for a single appointment; the api answers the detail of the
 * first occurrence. scope is 'this' (the default, when absent) | 'following' |
 * 'all', for an occurrence of a series; fields.recurrence goes with 'following'
 * or 'all' only. A deletion answers { id, visitBack: false, deleted: n }. The
 * demo knows no series: it ignores recurrence and scope.
 * Errors of a series: 400 INVALID_RECURRENCE, INVALID_SCOPE, SERIES_RULE_LOCKED
 * (a Google rule out of what Jobbiz writes), 409 SERIES_UNAVAILABLE.
 *
 * Its status (setAppointmentDone) and its comments (listAppointmentComments,
 * addAppointmentComment) are listed in crmApi.js; the demo keeps the comments
 * of an appointment under the task id `a:<id>`.
 */

const COMPANY_FIELDS = ['id', 'name', 'city', 'contactName', 'contactRole', 'phone', 'email'];
const uid = (prefix) => `${prefix}${Date.now()}${Math.random().toString(16).slice(2, 6)}`;

const found = (db, id) => {
  const item = db.appointments.find((appointment) => appointment.id === id);
  if (!item) throw new Error('Ce rendez-vous n’existe plus.');
  return item;
};

// What the history of an appointment says: created, then each status.
const historyOf = (item) => [
  { id: `${item.id}-created`, kind: 'created', at: item.createdAt ?? item.startsAt },
  ...(item.history ?? []),
];

const planningView = (db, me, { from, to }) => {
  const start = new Date(from);
  const end = new Date(to);
  const inRange = (date) => date >= start && date < end;
  const mine = db.tasks.filter((task) => task.assigneeId === me.id);
  const weekTasks = mine.filter((task) => task.dueOn >= isoDay(start) && task.dueOn < isoDay(end));
  return {
    events: db.appointments
      .filter((item) => inRange(new Date(item.startsAt)))
      .map((item) => withCompany(db, item, COMPANY_FIELDS)),
    // Its own appointment or callback already shows a task tied to one.
    tasks: weekTasks.filter((task) => !task.appointmentId && !task.callbackId).map((task) => withCompany(db, task, ['id', 'name'])),
    visits: (db.visits ?? []).map((item) => withCompany(db, item, COMPANY_FIELDS)),
    weekTasks: { done: weekTasks.filter((task) => task.doneAt).length, total: weekTasks.length },
    weekCalls: {
      done: db.calls.made.filter((call) => call.result === 'answered').length,
      total: db.calls.made.length,
    },
  };
};

const crmPlanningApi = {
  getPlanning: (payload, resolve, reject) => read(payload, resolve, reject, (db, me) => planningView(db, me, payload)),

  // A visit placed in the week becomes an appointment; its reason, its note.
  placeVisit: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const visit = (db.visits ?? []).find((item) => item.id === payload.visitId);
      if (!visit) throw new Error('Cette visite n’est plus à placer.');
      const company = companyOf(db, visit.companyId);
      const startsAt = new Date(payload.startsAt);
      const item = {
        id: uid('a'),
        category: visit.category,
        title: '',
        description: visit.reason,
        location: company?.city ?? '',
        startsAt: startsAt.toISOString(),
        endsAt: new Date(startsAt.getTime() + (visit.durationMin || 45) * 60000).toISOString(),
        status: 'planned',
        contactName: company?.contactName ?? '',
        companyId: visit.companyId,
        travelMinutes: payload.travelMinutes ?? null,
        visit,
        createdAt: new Date().toISOString(),
      };
      db.visits = db.visits.filter((other) => other.id !== visit.id);
      db.appointments.push(item);
      return withCompany(db, item, COMPANY_FIELDS);
    }),

  removeAppointment: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const item = found(db, payload.id);
      db.appointments = db.appointments.filter((other) => other.id !== item.id);
      if (item.visit) db.visits = [...(db.visits ?? []), item.visit];
      return { id: item.id, visitBack: Boolean(item.visit) };
    }),

  createAppointment: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const company = companyOf(db, payload.companyId);
      const item = {
        id: uid('a'),
        category: payload.category ?? 'interne',
        title: payload.title?.trim() ?? '',
        description: payload.description?.trim() ?? '',
        location: company?.city ?? '',
        startsAt: payload.startsAt,
        endsAt: payload.endsAt,
        status: 'planned',
        contactName: company?.contactName ?? '',
        companyId: payload.companyId ?? null,
        createdAt: new Date().toISOString(),
      };
      db.appointments.push(item);
      return withCompany(db, item, COMPANY_FIELDS);
    }),

  getAppointment: (payload, resolve, reject) =>
    read(payload, resolve, reject, (db) => {
      const item = found(db, payload.id);
      return {
        ...withCompany(db, item, COMPANY_FIELDS),
        history: historyOf(item).map((event) => ({ ...event, actor: event.actorId ? personOf(db, event.actorId) : null })),
      };
    }),

  updateAppointment: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db, me) => {
      const item = found(db, payload.id);
      const { status, outcome } = payload.fields;
      if (status && status !== item.status) {
        item.history = [...(item.history ?? []), { id: uid('h'), kind: status, at: new Date().toISOString(), actorId: me.id }];
        item.status = status;
      }
      if (outcome !== undefined) item.outcome = outcome;
      // The edit form of the panel: title, slot and description, as the api.
      const edits = ['title', 'startsAt', 'endsAt', 'description'].filter(
        (key) => payload.fields[key] !== undefined && payload.fields[key] !== item[key],
      );
      if (edits.length) {
        edits.forEach((key) => {
          item[key] = payload.fields[key];
        });
        item.history = [...(item.history ?? []), { id: uid('h'), kind: 'updated', at: new Date().toISOString(), actorId: me.id }];
      }
      return withCompany(db, item, COMPANY_FIELDS);
    }),

  respondAppointment: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const item = found(db, payload.id);
      item.myResponse = payload.response;
      return { id: item.id, response: payload.response };
    }),
};

export default crmPlanningApi;
